<script lang="ts">
  import { onMount } from 'svelte';
  import { supabase } from '../lib/supabase';
  import { navigationActions } from '../stores/navigation';
  import { selectedTargetLanguage, flashcardActions, categories, filteredCategories } from '../stores/flashcards';
  import { userSettings } from '../stores/settings';
  import TargetLanguageDialog from './dialogs/TargetLanguageDialog.svelte';
  import ArticleTTSPlayer from './ArticleTTSPlayer.svelte';
  import {
    ChevronLeft,
    Archive,
    Trash2,
    ZoomIn,
    ZoomOut,
    X,
    Save,
    BookmarkPlus,
    Volume2,
    Sparkles,
    Pencil,
    Loader2,
  } from 'lucide-svelte';
  import Button from './ui/button.svelte';
  import Card from './ui/card.svelte';
  import { _ } from 'svelte-i18n';
  import { resolveApiUrl } from '../lib/utils';

  interface Props {
    textId: string;
  }

  let { textId }: Props = $props();

  interface Text {
    id: string;
    title: string;
    content: string;
    summary: string | null;
    language: string;
    url: string | null;
    archived: boolean;
    font_size: number;
    word_count: number;
    tags?: { tag: string }[];
    words?: { id: string; word: string; translation: string | null }[];
  }

  let text = $state<Text | null>(null);
  let isLoading = $state(true);
  let error = $state('');
  let fontSize = $state(3);
  let isProcessMode = $state(true); // Always in process mode
  let markedWords = $state<Set<string>>(new Set());
  let showTargetLanguageModal = $state(false);
  let showTTSPlayer = $state(false);

  // Modal state
  let showWordModal = $state(false);
  let selectedWord = $state('');
  let isTranslating = $state(false);
  let translation = $state('');
  let translationSource = $state<string>('none');
  const translationSourceLabel = $derived.by(() => {
    switch (translationSource) {
      case 'deepl':
        return 'DeepL';
      case 'google':
        return 'Google Translate';
      case 'openai':
        return 'OpenAI';
      default:
        return 'AI';
    }
  });
  let selectedCategoryId = $state<string | null>(null);
  let isReadOnlyMode = $state(false);
  let existingWordId = $state<string | null>(null);
  let targetLanguageCode = $state<string>('EN');
  let primaryLanguageCode = $state<string>('CS');





  async function loadText() {
    isLoading = true;
    error = '';

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        error = $_('texts.view.must_be_logged_in');
        return;
      }

      const { data, error: fetchError } = await (supabase
        .from('texts' as any)
        .select('*, tags:text_tags(tag), words:text_words(id, word, translation)')
        .eq('id', textId)
        .eq('user_id', user.id)
        .single() as any);

      if (fetchError || !data) {
        error = $_('texts.view.text_not_found');
        console.error('Error loading text:', fetchError);
        return;
      }

      text = data as Text;
      fontSize = data.font_size || 3;

      // Build set of marked words
      if (data.words) {
        markedWords = new Set(data.words.map((w: any) => w.word.toLowerCase()));
      }
      await (supabase
        .from('texts' as any)
        .update({ read_at: new Date().toISOString() })
        .eq('id', textId) as any);

      // Auto-create category for this text if it doesn't exist (for legacy texts)
      await ensureCategoryExists(user.id, text);

    } catch (err) {
      console.error('Failed to load text:', err);
      error = $_('texts.view.load_error');
    } finally {
      isLoading = false;
    }
  }

  async function ensureCategoryExists(userId: string, textData: Text) {
    try {
      // Get target language ID from the text's language
      const { data: targetLangs } = await supabase
        .from('flashcard_target_languages')
        .select('id')
        .eq('code', textData.language)
        .maybeSingle();

      if (!targetLangs) {
        console.warn('Target language not found for:', textData.language);
        return;
      }

      // Check if category with this name already exists
      const { data: existingCategory } = await supabase
        .from('flashcard_categories')
        .select('id')
        .eq('user_id', userId)
        .eq('name', textData.title)
        .eq('target_language_id', targetLangs.id)
        .maybeSingle();

      // Create category only if it doesn't exist
      if (!existingCategory) {
        const { error: categoryError } = await supabase
          .from('flashcard_categories')
          .insert({
            user_id: userId,
            name: textData.title.substring(0, 255),
            description: textData.url || null,
            target_language_id: targetLangs.id,
          });

        if (categoryError) {
          console.warn('Could not create category for text:', categoryError);
        } else {
          console.log('Created category for legacy text:', textData.title);
        }
      }
    } catch (err) {
      console.warn('Error ensuring category exists:', err);
    }
  }

  async function updateFontSize(delta: number) {
    const newSize = Math.max(1, Math.min(5, fontSize + delta));
    fontSize = newSize;

    if (text) {
      await (supabase
        .from('texts' as any)
        .update({ font_size: newSize })
        .eq('id', textId) as any);
    }
  }

  async function toggleArchive() {
    if (!text) return;

    const { error } = await (supabase
      .from('texts' as any)
      .update({ archived: !text.archived })
      .eq('id', textId) as any);

    if (!error) {
      text.archived = !text.archived;
    }
  }

  async function deleteText() {
    if (!text) return;

    if (!confirm($_('texts.view.delete_confirm', { values: { title: text.title } }))) {
      return;
    }

    const { error } = await (supabase
      .from('texts' as any)
      .delete()
      .eq('id', textId) as any);

    if (!error) {
      navigationActions.goToTexts();
    }
  }

  function handleWordClick(event: MouseEvent) {
    if (!isProcessMode || !text) return;

    const target = event.target as HTMLElement;
    if (target.classList.contains('word')) {
      const word = target.textContent?.trim() || '';
      if (word) {
        openWordModal(word);
      }
    }
  }

  async function openWordModal(word: string) {
    selectedWord = word;
    translation = '';
    selectedCategoryId = null;
    isReadOnlyMode = false;
    existingWordId = null;
    
    // Set language codes dynamically
    targetLanguageCode = (text?.language || 'en').toUpperCase();
    primaryLanguageCode = ($userSettings?.preferred_language || 'cs').toUpperCase();
    
    // Check if word already exists in text.words
    if (text?.words) {
      const existingWord = text.words.find(w => w.word.toLowerCase() === word.toLowerCase());
      if (existingWord && existingWord.translation) {
        // Word already translated - show in read-only mode
        translation = existingWord.translation;
        isReadOnlyMode = true;
        existingWordId = existingWord.id;
        showWordModal = true;
        return;
      }
    }

    showWordModal = true;

    // Load categories if not loaded
    await flashcardActions.loadData();

    // Try to find or create default category for this text
    if (text) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: targetLangs } = await supabase
          .from('flashcard_target_languages')
          .select('id')
          .eq('code', text.language)
          .maybeSingle();

        if (targetLangs) {
          // Find existing category with text title
          const existingCategory = $filteredCategories.find(
            cat => cat.name === text!.title && cat.targetLanguage.id === targetLangs.id
          );

          if (existingCategory) {
            selectedCategoryId = existingCategory.id;
          }
        }
      }
    }

    // Auto-translate using existing translate API
    autoTranslate(word);
  }

  async function autoTranslate(word: string) {
    if (!text) return;

    isTranslating = true;
    translationSource = 'none';
    try {
      const primaryLanguage = $userSettings?.preferred_language || 'cs';
      const response = await fetch(resolveApiUrl('/api/translate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: word,
          sourceLanguage: text.language,
          targetLanguage: primaryLanguage,
        }),
      });

      const data = await response.json();

      if (data.translations && data.translations.length > 0) {
        translation = data.translations[0];
        translationSource = data.source;
      }
    } catch (err) {
      console.error('Translation error:', err);
    } finally {
      isTranslating = false;
    }
  }

  function updateMarkedWords() {
    if (text?.words) {
      markedWords = new Set(text.words.map((w: any) => w.word.toLowerCase()));
    }
  }

  async function saveWord() {
    if (!selectedWord || !text) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if we're updating an existing word or creating a new one
      if (existingWordId) {
        // UPDATE existing word translation
        const { error: updateError } = await (supabase
          .from('text_words' as any)
          .update({
            translation: translation || null,
          })
          .eq('id', existingWordId) as any);

        if (!updateError) {
          // Update local state
          if (text.words) {
            const wordIndex = text.words.findIndex(w => w.id === existingWordId);
            if (wordIndex !== -1) {
              text.words[wordIndex].translation = translation || null;
              text.words = [...text.words]; // Trigger reactivity
            }
          }

          showWordModal = false;
          selectedWord = '';
          translation = '';
          selectedCategoryId = null;
          isReadOnlyMode = false;
          existingWordId = null;
        } else {
          console.error('Error updating word:', updateError);
        }
        return;
      }

      // CREATE new word (existing logic)
      // Get target language ID from the text's language
      let targetLangId: string | null = null;

      try {
        const { data: targetLangs } = await supabase
          .from('flashcard_target_languages')
          .select('id')
          .eq('code', text.language)
          .maybeSingle();

        if (targetLangs) {
          targetLangId = targetLangs.id;
        }
      } catch (err) {
        console.warn('Could not fetch target language:', err);
      }

      if (!targetLangId) {
        console.error('Target language not found for text language:', text.language);
        return;
      }

      // Use selected category or create default one
      let categoryId: string | null = selectedCategoryId;

      if (!categoryId) {
        // Create a new category with the text title as fallback
        try {
          const { data: newCategory, error: categoryError } = await supabase
            .from('flashcard_categories')
            .insert({
              user_id: user.id,
              name: text.title.substring(0, 255),
              target_language_id: targetLangId,
              description: text.url || null,
            })
            .select()
            .maybeSingle();

          if (!categoryError && newCategory) {
            categoryId = newCategory.id;
          }
        } catch (err) {
          console.warn('Error creating category:', err);
        }
      }

      // Create flashcard from word with category
      const { data: flashcard, error: flashcardError } = await supabase
        .from('flashcards')
        .insert({
          user_id: user.id,
          primary_text: translation || '',  // Translation in primary language (Czech)
          target_text: selectedWord,        // Foreign language word (clicked word from text)
          target_language_id: targetLangId,
          category_id: categoryId,
        })
        .select()
        .single();

      if (flashcardError || !flashcard) {
        console.error('Error creating flashcard:', flashcardError);
        return;
      }

      // Mark word in text
      const { error } = await (supabase
        .from('text_words' as any)
        .insert({
          text_id: textId,
          word: selectedWord,
          translation: translation || null,
          vocabulary_card_id: flashcard.id,
          category_id: categoryId,
        }) as any);

      if (!error) {
        // Update local state immediately - create new Set to trigger reactivity
        markedWords = new Set([...markedWords, selectedWord.toLowerCase()]);

        // Also update text.words for real-time reflection
        if (!text.words) {
          text.words = [];
        }
        text.words = [...text.words, {
          id: flashcard.id,
          word: selectedWord,
          translation: translation || null,
        }];

        showWordModal = false;
        selectedWord = '';
        translation = '';
        selectedCategoryId = null;

        // Reload categories to include newly created one
        await flashcardActions.loadData();
      }
    } catch (err) {
      console.error('Error saving word:', err);
    }
  }

  // Use summary if available, otherwise fall back to content
  const displayContent = $derived(text?.summary || text?.content || '');

  const renderedContent = $derived.by(() => {
    if (!text) return '';

    // Force reactivity by accessing markedWords.size
    const markedWordsSize = markedWords.size;

    // Split content into words and wrap each in a span
    // Use summary for display if available
    const contentToRender = text.summary || text.content;
    
    return contentToRender
      .split(/\s+/)
      .map((word) => {
        const cleanWord = word.toLowerCase().replace(/[.,!?;:]/g, '');
        const isMarked = markedWords.has(cleanWord);

        if (isProcessMode) {
          const baseClass = 'word cursor-pointer rounded px-1';
          if (isMarked) {
            // Use inline styles for marked words to ensure visibility
            return `<span class="${baseClass}" style="background-color: hsla(var(--primary), 0.2); color: hsl(var(--primary)); font-weight: 600;">${word}</span>`;
          } else {
            return `<span class="${baseClass}" style="transition: background-color 0.2s;">${word}</span>`;
          }
        }
        return `<span>${word}</span>`;
      })
      .join(' ');
  });

  const fontSizeClasses = [
    'text-sm',
    'text-base',
    'text-lg',
    'text-xl',
    'text-2xl',
  ];

  const LANGUAGE_FLAG_MAP: Record<string, string> = {
    en: 'gb',
    es: 'es',
    fr: 'fr',
    de: 'de',
    it: 'it',
    pt: 'pt',
    ru: 'ru',
    zh: 'cn',
    ja: 'jp',
    ko: 'kr',
    ar: 'sa',
    cs: 'cz',
    pl: 'pl',
    tr: 'tr',
    uk: 'ua',
    th: 'th',
    vi: 'vn',
    id: 'id',
    hi: 'in',
  };

  const getFlagCode = (code: string): string => {
    const normalized = code?.toLowerCase() ?? '';
    return LANGUAGE_FLAG_MAP[normalized] ?? normalized;
  };

  onMount(() => {
    loadText();
  });
</script>

<div class="min-h-screen pb-24 bg-gradient-subtle">
  <div class="mx-auto max-w-3xl px-4 py-6 space-y-6">
              <button
            type="button"
            onclick={() => navigationActions.goToTexts()}
            class="btn btn-back"
            aria-label={$_('buttons.back_to_texts')}
          >
            <ChevronLeft  class="h-5 w-5" />
          </button>

    {#if isLoading}
      <Card class="p-12 text-center">
        <div class="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
        <p class="mt-4">{$_('texts.view.loading')}</p>
      </Card>
    {:else if error}
      <Card class="p-12 text-center">
        <p class="text-destructive">{error}</p>
        <div class="mt-4 flex justify-center">
          <button type="button" onclick={() => navigationActions.goToTexts()} class="btn btn-back" aria-label={$_('buttons.back_to_texts')}>
            <ChevronLeft  class="h-5 w-5" />
          </button>
        </div>
      </Card>
    {:else if text}
      <!-- Language Selection Widget -->
      

      <!-- Navigation Bar -->
      <Card class="p-4">
        <div class="flex items-center justify-end gap-2">
          <!-- Font Size -->
          <button
            onclick={() => updateFontSize(-1)}
            disabled={fontSize <= 1}
            class="btn btn-ghost btn-sm h-10 w-10 p-0"
          >
            <ZoomOut class="h-4 w-4" />
          </button>
          <button
            onclick={() => updateFontSize(1)}
            disabled={fontSize >= 5}
            class="btn btn-ghost btn-sm h-10 w-10 p-0"
          >
            <ZoomIn class="h-4 w-4" />
          </button>

          <!-- Text-to-Speech -->
          <button
            onclick={() => showTTSPlayer = !showTTSPlayer}
            class="btn btn-ghost btn-sm h-10 w-10 p-0"
            class:text-primary={showTTSPlayer}
            title={$_('tts.read_article')}
          >
            <Volume2 class="h-4 w-4" />
          </button>

          <!-- Archive -->
          <button
            onclick={toggleArchive}
            class="btn btn-ghost btn-sm h-10 w-10 p-0"
            title={text.archived ? $_('texts.view.restore_button') : $_('texts.view.archive_button')}
          >
            <Archive class="h-4 w-4" />
          </button>

          <!-- Delete -->
          <button
            onclick={deleteText}
            class="btn btn-destructive btn-sm h-10 w-10 p-0"
            title={$_('texts.view.delete_button')}
          >
            <Trash2 class="h-4 w-4" />
          </button>
        </div>
      </Card>

      <!-- TTS Player -->
      {#if showTTSPlayer && text}
        <ArticleTTSPlayer 
          text={text.content} 
          onClose={() => showTTSPlayer = false} 
        />
      {/if}

      <!-- Text Content -->
      <Card class="p-8">
        <h1 class="text-3xl font-bold mb-6">
          {text.title}
        </h1>

        {#if text.summary}
          <div class="mb-4 flex items-center gap-2 text-primary text-sm">
            <Sparkles class="h-4 w-4" />
            <span>{$_('texts.view.summary_indicator')}</span>
          </div>
        {/if}

        {#if text.url}
          <div class="mb-6 text-sm text-muted-foreground">
            <a href={text.url} target="_blank" rel="noopener noreferrer" class="hover:text-primary underline">
              {text.url}
            </a>
          </div>
        {/if}

        <div
          class="{fontSizeClasses[fontSize - 1]} leading-relaxed whitespace-pre-wrap"
          onclick={handleWordClick}
          onkeydown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              const target = e.target as HTMLElement;
              if (target.classList.contains('word')) {
                const word = target.textContent?.trim() || '';
                if (word) {
                  openWordModal(word);
                }
              }
            }
          }}
          role="button"
          tabindex={isProcessMode ? 0 : -1}
        >
          {@html renderedContent}
        </div>

        {#if text.tags && text.tags.length > 0}
          <div class="flex flex-wrap gap-2 mt-8 pt-6 border-t border-border">
            {#each text.tags as { tag }}
              <span class="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-muted text-sm">
                {tag}
              </span>
            {/each}
          </div>
        {/if}
      </Card>

      <!-- Word Modal -->
      {#if showWordModal}
        <div class="fixed inset-0 z-[50] flex items-center justify-center bg-black/50 p-4">
          <div class="w-full max-w-md p-6 space-y-4 relative z-[96] rounded-2xl border border-border shadow-lg"
               style="background-color: hsl(var(--card));">

            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold">
                {isReadOnlyMode ? $_('texts.view.translation_label') : $_('texts.view.add_word')}
              </h3>
              <div class="flex gap-2 items-center">
                {#if isReadOnlyMode}
                  <button
                    onclick={() => isReadOnlyMode = false}
                    class="text-primary hover:text-primary/80"
                    title="Edit"
                  >
                    <Pencil class="h-5 w-5" />
                  </button>
                {/if}
                <div class="flex-1"></div>
                <button
                  onclick={() => showWordModal = false}
                  class="btn btn-close"
                >
                  <X class="h-5 w-5" />
                </button>
              </div>
            </div>

            {#if isReadOnlyMode}
              <!-- Read-only mode -->
              <div>
                <div>
                  <div class="text-lg font-semibold mb-3">{targetLanguageCode}: {selectedWord}</div>
                </div>
                <div class="flex-1">
                  <div class="text-lg font-semibold mb-1">{primaryLanguageCode}: {translation}</div>
                </div>
              </div>
            {:else}
              <!-- Edit mode -->
              <div>
              <label for="word-display" class="block text-sm font-medium mb-2">
                {targetLanguageCode}
              </label>
              <div id="word-display" class="text-2xl font-bold text-primary mb-4">
                {selectedWord}
              </div>
            </div>
              <div>
                <label for="translation-input" class="block text-sm font-medium mb-2">
                  {primaryLanguageCode}
                  {#if translationSource && translationSource !== 'none'}
                    <span class="text-xs text-muted-foreground font-normal ml-2">
                       (via {translationSourceLabel})
                    </span>
                  {/if}
                </label>
                <div class="flex gap-2">
                  <div class="relative flex-1">
                    <input
                      id="translation-input"
                      type="text"
                      bind:value={translation}
                      placeholder={$_('texts.view.translation_placeholder')}
                      disabled={isTranslating}
                      class="form-input w-full disabled:opacity-50"
                    />
                    {#if isTranslating}
                      <div class="absolute right-3 top-1/2 -translate-y-1/2">
                        <div class="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
                      </div>
                    {/if}
                  </div>
                  <Button
                    onclick={() => autoTranslate(selectedWord)}
                    disabled={isTranslating || !selectedWord}
                    class="btn btn-action px-4"
                    variant="outline"
                  >
                    {isTranslating ? '...' : $_('buttons.translate')}
                  </Button>
                </div>
              </div>

              <div>
                <label for="category-select" class="block text-sm font-medium mb-2">
                  {$_('category_label')}
                </label>
                <select
                  id="category-select"
                  bind:value={selectedCategoryId}
                  class="form-input w-full"
                >
                  <option value={null}>Vytvořit novou kategorii "{text?.title}"</option>
                  {#each $filteredCategories as category}
                    <option value={category.id}>{category.name}</option>
                  {/each}
                </select>
              </div>
            {/if}

            <div class="flex gap-3 pt-4">
              {#if !isReadOnlyMode}
                <Button
                onclick={() => showWordModal = false}
                variant="outline"
                class="flex-1"
                >
                {$_('buttons.cancel')}
                </Button>
                <Button
                onclick={saveWord}
                variant="default"
                class="flex-1 {isTranslating ? 'opacity-50 pointer-events-none' : ''}"
                disabled={isTranslating}
                >
                {#if isTranslating}
                  <Loader2 class="h-4 w-4 mr-2 animate-spin" />
                  Překládám...
                {:else}
                  <Save class="h-4 w-4 mr-2" />
                  {$_('buttons.save')}
                {/if}
                </Button>
              {/if}
            </div>
          </div>
        </div>
      {/if}
    {/if}
  </div>
</div>

<TargetLanguageDialog isOpen={showTargetLanguageModal} onClose={() => (showTargetLanguageModal = false)} />
